import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  NativeSelect,
  Textarea,
  useToast,
} from '@provenance/design';
import { ALL_WEBHOOK_EVENTS, INTEGRATOR_OPERATIONS } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plug, ShieldAlert, Webhook } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { SecretReveal } from './ApiKeyDialogs';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * INTEGRATOR ONBOARDING — spec v3 §20.3
 *
 * The three steps that precede a key: the integrator itself, a chamber grant, and a
 * webhook endpoint. Each is its own dialog because each is its own decision — the
 * relationship, the access, the callback — and each can be changed without the others.
 * ════════════════════════════════════════════════════════════════════════════
 */

const toSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);

export function OnboardIntegratorDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [description, setDescription] = useState('');
  const [rateLimit, setRateLimit] = useState('600');

  const reset = () => {
    setName('');
    setSlug('');
    setSlugTouched(false);
    setContactName('');
    setContactEmail('');
    setWebsiteUrl('');
    setDescription('');
    setRateLimit('600');
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/api/console/platform/integrators', {
        name: name.trim(),
        slug,
        contactName: contactName.trim() || null,
        contactEmail: contactEmail.trim(),
        websiteUrl: websiteUrl.trim() || null,
        description: description.trim() || null,
        defaultRateLimitPerMinute: Number(rateLimit) || 600,
      }),
    onSuccess: (row) => {
      void queryClient.invalidateQueries({ queryKey: ['integrators'] });
      toast.success('Integrator onboarded', 'Next: grant it a chamber, then issue a key.');
      reset();
      onOpenChange(false);
      onCreated(row.id);
    },
    onError: (error) =>
      toast.error('Could not onboard the integrator', error instanceof ApiError ? error.message : ''),
  });

  const valid = name.trim().length >= 2 && slug.length >= 2 && /.+@.+\..+/.test(contactEmail);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader
          eyebrow="Integrators"
          title="Onboard an integrator"
          description="This is the commercial relationship, not a credential. Chamber access and keys come next."
        />
        <DialogBody className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name" required>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slugTouched) setSlug(toSlug(e.target.value));
                }}
                placeholder="SageDocs"
                autoFocus
              />
            </Field>
            <Field
              label="Identifier"
              required
              help="Lowercase letters, numbers and hyphens. Cannot be changed later."
            >
              <Input
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value.toLowerCase());
                }}
                className="pv-mono"
                placeholder="sagedocs"
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Technical contact" help="Who we call when an integration misbehaves.">
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </Field>
            <Field label="Contact email" required>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Website">
              <Input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://"
              />
            </Field>
            <Field
              label="Requests per minute"
              help="Default for every key. Bursts are allowed above this briefly."
            >
              <Input
                type="number"
                min={1}
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Notes">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            loading={create.isPending}
            onClick={() => create.mutate()}
            leadingIcon={<Plug className="size-3.5" />}
          >
            Onboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ChamberOption {
  id: string;
  slug: string;
  shortName: string;
  legalName: string;
  active: boolean;
  sandbox: boolean;
}

export function GrantChamberDialog({
  integratorId,
  integratorName,
  grantedChamberSlugs,
  open,
  onOpenChange,
}: {
  integratorId: string;
  integratorName: string;
  grantedChamberSlugs: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [chamberId, setChamberId] = useState('');
  const [operations, setOperations] = useState<string[]>([...INTEGRATOR_OPERATIONS]);

  const chambers = useQuery({
    queryKey: ['platform-chambers'],
    queryFn: () => api.get<ChamberOption[]>('/api/console/platform/chambers'),
    enabled: open,
  });
  const options = (chambers.data ?? []).filter(
    (c) => c.active && !grantedChamberSlugs.includes(c.slug),
  );

  const close = () => {
    setChamberId('');
    setOperations([...INTEGRATOR_OPERATIONS]);
    onOpenChange(false);
  };

  const grant = useMutation({
    mutationFn: () =>
      api.post(`/api/console/platform/integrators/${integratorId}/grants`, {
        chamberId,
        organisationScope: 'ALL',
        operations,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrator', integratorId] });
      toast.success('Access granted', `${integratorName} can now act for that chamber.`);
      close();
    },
    onError: (error) =>
      toast.error('Could not grant access', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader
          eyebrow={integratorName}
          title="Grant access to a chamber"
          description="Without a grant every request is refused with chamber_not_granted, whatever key it carries."
        />
        <DialogBody className="flex flex-col gap-4">
          <Field
            label="Chamber"
            required
            help={
              chambers.isLoading
                ? 'Loading chambers…'
                : options.length === 0
                  ? 'Every active chamber is already granted.'
                  : undefined
            }
          >
            <NativeSelect value={chamberId} onChange={(e) => setChamberId(e.target.value)}>
              <option value="">Choose a chamber…</option>
              {options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.shortName} — {c.legalName}
                  {c.sandbox ? ' (sandbox)' : ''}
                </option>
              ))}
            </NativeSelect>
          </Field>

          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-2 [font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]">
              Operations
            </legend>
            <div className="flex flex-col gap-2 rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-card-alt)] p-3.5">
              {INTEGRATOR_OPERATIONS.map((op) => (
                <Checkbox
                  key={op}
                  checked={operations.includes(op)}
                  onCheckedChange={(checked) =>
                    setOperations((prev) =>
                      checked === true ? [...prev, op] : prev.filter((o) => o !== op),
                    )
                  }
                  label={<span className="pv-mono [font-size:12.5px]">{op}</span>}
                />
              ))}
            </div>
          </fieldset>
          <p className="m-0 [font-size:var(--pv-text-xs)] text-[var(--pv-faint)]">
            The grant covers all of the chamber's exporters and whatever document types the
            chamber has enabled. A key can narrow this further, never widen it.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!chamberId || operations.length === 0}
            loading={grant.isPending}
            onClick={() => grant.mutate()}
          >
            Grant access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddWebhookDialog({
  integratorId,
  integratorName,
  open,
  onOpenChange,
}: {
  integratorId: string;
  integratorName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [events, setEvents] = useState<string[]>([...ALL_WEBHOOK_EVENTS]);
  const [secret, setSecret] = useState<string | null>(null);

  const reset = () => {
    setUrl('');
    setDescription('');
    setEvents([...ALL_WEBHOOK_EVENTS]);
    setSecret(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ id: string; signingSecret: string }>(
        `/api/console/platform/integrators/${integratorId}/webhooks`,
        { url: url.trim(), description: description.trim() || null, events, chamberId: null },
      ),
    onSuccess: (row) => {
      setSecret(row.signingSecret);
      void queryClient.invalidateQueries({ queryKey: ['integrator', integratorId] });
    },
    onError: (error) =>
      toast.error('Could not add the endpoint', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // The signing secret is shown once; closing from anywhere but the button would lose it.
        if (!next && secret) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        {secret ? (
          <>
            <DialogHeader
              eyebrow={integratorName}
              title="Copy the signing secret now"
              description="Shown once. Only a hash is stored, so it cannot be retrieved later — rotate it if it is lost."
            />
            <DialogBody className="flex flex-col gap-4">
              <Callout
                tone="warn"
                icon={<ShieldAlert className="size-4" />}
                title="Treat this like a password"
              >
                {integratorName} uses it to verify that webhook events really came from us.
              </Callout>
              <Field label="Signing secret">
                <SecretReveal value={secret} />
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="primary"
                onClick={() => {
                  reset();
                  onOpenChange(false);
                  toast.success('Webhook endpoint added', 'Use Test to confirm it answers.');
                }}
              >
                I have copied it
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader
              eyebrow={integratorName}
              title="Add a webhook endpoint"
              description="Where we tell the integrator a certificate was received, approved, rejected, revoked or needs clarification."
            />
            <DialogBody className="flex flex-col gap-4">
              <Field label="Endpoint URL" required help="HTTPS is required outside local development.">
                <Input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://"
                  className="pv-mono"
                  autoFocus
                />
              </Field>
              <Field label="Description">
                <Input value={description} onChange={(e) => setDescription(e.target.value)} />
              </Field>
              <fieldset className="m-0 border-0 p-0">
                <legend className="mb-2 [font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]">
                  Events
                </legend>
                <div className="flex flex-col gap-2 rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-card-alt)] p-3.5">
                  {ALL_WEBHOOK_EVENTS.map((event) => (
                    <Checkbox
                      key={event}
                      checked={events.includes(event)}
                      onCheckedChange={(checked) =>
                        setEvents((prev) =>
                          checked === true ? [...prev, event] : prev.filter((e) => e !== event),
                        )
                      }
                      label={<span className="pv-mono [font-size:12.5px]">{event}</span>}
                    />
                  ))}
                </div>
              </fieldset>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!/^https?:\/\/.+/.test(url.trim()) || events.length === 0}
                loading={create.isPending}
                onClick={() => create.mutate()}
                leadingIcon={<Webhook className="size-3.5" />}
              >
                Add endpoint
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
